<script setup lang="ts">
import type { AvatarAssignedMessage } from '@ilteducation/quizter-types';
import { JoinedPlayer } from '../stores/quizterStore';
import useLocale from '../../composables/use-locale';

type Props = {
  avatar: AvatarAssignedMessage['payload']['avatar'];
  player?: JoinedPlayer;
  isActive?: boolean;
  size?: 'xsmall' | 'headerSmall' | 'small' | 'medium' | 'large';
  pulse: boolean;
  showRemoveButton?: boolean;
  isRemoving?: boolean;
};

withDefaults(defineProps<Props>(), {
  isActive: true,
  size: 'medium',
  pulse: false,
  showRemoveButton: false,
  isRemoving: false,
});

const emits = defineEmits<{
  (e: 'remove', playerId: string): void;
}>();

const { t } = useLocale();

const sizeMap = {
  xsmall: 40,
  headerSmall: 55,
  small: 80,
  medium: 100,
  large: 120,
};
</script>

<template>
  <div
    class="avatar-container position-relative overflow-visible"
    :class="{ inactive: !isActive, active: isActive, pulse: pulse }"
    :style="{ width: `${sizeMap[size]}px`, height: `${sizeMap[size]}px` }"
  >
    <img :src="avatar.image" alt="Avatar" class="avatar-image" />
    <v-btn v-if="showRemoveButton" class="remove-button bg-secondary" @click="emits('remove', player!.id)">
      <v-progress-circular v-if="isRemoving" indeterminate size="14" width="2" />
      <v-icon v-else icon="fa:fawsb fa-xmark"></v-icon>
    </v-btn>
    <v-tooltip v-if="!isActive" activator="parent" location="top" aria-hidden="true">
      <span>{{ t('lobby.tooltip_player_offline') }}</span>
    </v-tooltip>
  </div>
</template>

<style scoped lang="scss">
@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.remove-button {
  display: none;
  position: absolute;
  top: -12px;
  right: -12px;
  min-width: 24px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  padding: 0;

  .avatar-container:hover & {
    display: block;
  }

  .avatar-container.inactive & {
    display: block;
  }
}

.avatar-container {
  border-radius: 12px;
  overflow: hidden;
  border: 3px solid transparent;
  background-color: rgba(100, 100, 100, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &.inactive {
    .avatar-image {
      filter: grayscale(100%);
      opacity: 0.5;
    }
  }

  &.active {
    border-color: #2196f3;
    background-color: rgba(33, 150, 243, 0.1);

    .avatar-image {
      filter: none;
      opacity: 1;
    }
  }

  .avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition:
      filter 0.3s ease,
      opacity 0.3s ease;
  }
}
.pulse {
  animation: pulse 1.5s ease-in-out infinite;
}
</style>
